import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import client from '../api/client';
import { Project, Model } from '../types';
import './ProjectDetail.css';

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Ausstehend',
  PROCESSING: 'Wird verarbeitet',
  DONE: 'Fertig',
  ERROR: 'Fehler',
  NOT_REQUIRED: 'Kein Export',
};

const STATUS_CLASS: Record<string, string> = {
  PENDING: 'status-pending',
  PROCESSING: 'status-processing',
  DONE: 'status-done',
  ERROR: 'status-error',
  NOT_REQUIRED: 'status-neutral',
};

// Löst eine Signed URL vom Backend auf und startet den Browser-Download
async function triggerDownload(modelId: string, type: 'original' | 'glb', filename: string) {
  try {
    const r = await client.get(`/api/armundi/models/${modelId}/download?type=${type}`);
    const url = r.data.url;
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch {
    alert('Download fehlgeschlagen.');
  }
}

function UploadModal({ projectId, onClose, onSuccess }: {
  projectId: string;
  onClose: () => void;
  onSuccess: (m: Model) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [form, setForm] = useState({ name: '', street: '', streetNumber: '', zip: '', city: '' });
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const thumbRef = useRef<HTMLInputElement>(null);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setError('Bitte eine Datei auswählen.'); return; }
    setUploading(true);
    setError('');
    const fd = new FormData();
    fd.append('model', file);
    if (thumbnail) fd.append('thumbnail', thumbnail);
    fd.append('projectId', projectId);
    fd.append('displayName', form.name || file.name);
    fd.append('street', form.street);
    fd.append('houseNumber', form.streetNumber);
    fd.append('zipCode', form.zip);
    fd.append('city', form.city);
    try {
      const r = await client.post(`/api/armundi/models/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: e => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)),
      });
      onSuccess(r.data);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Upload fehlgeschlagen.');
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Modell hochladen</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {error && <div className="modal-error">{error}</div>}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Modelldatei * <span className="label-hint">(IFC, OBJ, FBX, GLB, Punktwolke)</span></label>
            <div
              className={`drop-zone ${dragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" hidden onChange={e => setFile(e.target.files?.[0] || null)} />
              {file ? (
                <div className="drop-file-name">
                  <span className="drop-file-icon">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M2 2h8l4 4v8a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" fill="var(--teal)" opacity=".3"/>
                      <path d="M10 2v4h4" stroke="var(--teal)" strokeWidth="1.2" fill="none"/>
                    </svg>
                  </span>
                  {file.name}
                  <button type="button" className="drop-remove" onClick={e => { e.stopPropagation(); setFile(null); }}>×</button>
                </div>
              ) : (
                <span className="drop-placeholder">Datei auswählen oder hierher ziehen</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Anzeigename</label>
            <input value={form.name} onChange={set('name')} placeholder="z.B. Hauptgebäude EG" />
          </div>

          <div className="form-row">
            <div className="form-group form-group-flex3">
              <label>Straße</label>
              <input value={form.street} onChange={set('street')} placeholder="Musterstraße" />
            </div>
            <div className="form-group form-group-flex1">
              <label>Nr.</label>
              <input value={form.streetNumber} onChange={set('streetNumber')} placeholder="12" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-flex1">
              <label>PLZ</label>
              <input value={form.zip} onChange={set('zip')} placeholder="44137" maxLength={5} />
            </div>
            <div className="form-group form-group-flex2">
              <label>Stadt</label>
              <input value={form.city} onChange={set('city')} placeholder="Dortmund" />
            </div>
          </div>

          <div className="form-group">
            <label>Titelbild <span className="label-hint">(optional)</span></label>
            <div className="thumb-zone" onClick={() => thumbRef.current?.click()}>
              <input ref={thumbRef} type="file" hidden accept="image/jpeg,image/png" onChange={e => setThumbnail(e.target.files?.[0] || null)} />
              {thumbnail ? (
                <span className="thumb-name">
                  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                    <rect x="1" y="3" width="14" height="10" rx="1.5" fill="var(--teal)" opacity=".3"/>
                    <circle cx="5.5" cy="6.5" r="1.5" fill="var(--teal)"/>
                    <path d="M1 11l4-3 3 3 2-2 5 5" stroke="var(--teal)" strokeWidth="1.2" fill="none"/>
                  </svg>
                  {thumbnail.name}
                  <button type="button" className="drop-remove" onClick={e => { e.stopPropagation(); setThumbnail(null); }}>×</button>
                </span>
              ) : (
                <span className="thumb-placeholder">JPG oder PNG auswählen</span>
              )}
            </div>
          </div>

          {uploading && (
            <div className="progress-wrap">
              <div className="progress-bar"><div className="progress-fill" style={{width: `${progress}%`}} /></div>
              <span className="progress-label">{progress}%</span>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={uploading}>Abbrechen</button>
            <button type="submit" className="btn-primary" disabled={uploading}>
              {uploading ? 'Wird hochgeladen...' : 'Hochladen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    client.get(`/api/armundi/projects/${id}`)
      .then(r => setProject(r.data.project || r.data))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="loading-state" style={{padding:'32px'}}>Wird geladen...</div>;
  if (!project) return <div style={{padding:'32px', color:'var(--text-muted)'}}>Projekt nicht gefunden.</div>;

  return (
    <div className="project-detail">
      <div className="breadcrumb">
        <Link to="/projects">Projekte</Link>
        <span>/</span>
        <span>{project.name}</span>
      </div>

      <div className="page-header">
        <div>
          <h1 className="page-title">{project.name}</h1>
          <p className="page-subtitle">{project.models?.length ?? 0} Modelle</p>
        </div>
        <button className="btn-primary" onClick={() => setShowUpload(true)}>+ Modell hochladen</button>
      </div>

      {project.description && <p className="project-description">{project.description}</p>}

      {project.models?.length === 0 ? (
        <div className="empty-state">
          <p className="empty-title">Noch keine Modelle vorhanden.</p>
          <p className="empty-sub">Laden Sie das erste Modell für dieses Projekt hoch.</p>
          <button className="btn-primary" style={{marginTop:'16px'}} onClick={() => setShowUpload(true)}>Modell hochladen</button>
        </div>
      ) : (
        <div className="models-grid">
          {project.models.map(m => (
            <div key={m.id} className="model-card">
              <div className="model-thumb">
                {m.thumbnailUrl
                  ? <img src={m.thumbnailUrl} alt={m.name} />
                  : <div className="model-thumb-placeholder">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                }
              </div>
              <div className="model-info">
                <div className="model-header">
                  <span className="model-name">{m.displayName || m.name}</span>
                  <span className={`model-status ${STATUS_CLASS[m.status || '']}`}>
                    {STATUS_LABEL[m.status || ''] || m.status}
                  </span>
                </div>
                <div className="model-meta">
                  <span className="model-type">{m.fileType}</span>
                  {m.city && <span className="model-address">{m.street} {m.streetNumber}, {m.zip} {m.city}</span>}
                </div>
                <span className="model-date">{m.createdAt ? new Date(m.createdAt).toLocaleDateString('de-DE') : ''}</span>

                {/* Download-Buttons – nur anzeigen wenn Datei vorhanden */}
                <div className="model-downloads">
                  {m.originalFileKey && (
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => triggerDownload(m.id, 'original', m.displayName || m.name || 'original')}
                    >
                      ↓ Original
                    </button>
                  )}
                  {m.glbKey && m.status === 'DONE' && (
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => triggerDownload(m.id, 'glb', (m.displayName || m.name || 'model') + '.glb')}
                    >
                      ↓ GLB
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadModal
          projectId={project.id}
          onClose={() => setShowUpload(false)}
          onSuccess={m => setProject(p => p ? { ...p, models: [...p.models, m] } : p)}
        />
      )}
    </div>
  );
}
